/**
 * P1-S1 — PIN lock primitives.
 *
 * The PIN is a convenience lock for a shared phone, not account security: it
 * is hashed (SHA-256 over a random per-device salt plus the PIN) and kept in
 * `localStorage.hc_pin` together with a wrong-attempt counter. Nothing here
 * ever logs or returns the PIN itself.
 *
 * Pure module (no React, no Redux) so the keypad, the settings sheet and the
 * lock hook share one implementation.
 */

export const PIN_STORAGE_KEY = 'hc_pin';
export const AUTOLOCK_STORAGE_KEY = 'hc_autolock_minutes';
export const PIN_CHANGE_EVENT = 'hc:pin-change';
export const LOCK_REQUEST_EVENT = 'hc:lock-request';

export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;
export const MAX_PIN_ATTEMPTS = 5;

export type AutoLockSetting = 1 | 5 | 15 | 'never';

export const AUTOLOCK_OPTIONS: readonly AutoLockSetting[] = [1, 5, 15, 'never'];
export const DEFAULT_AUTOLOCK: AutoLockSetting = 5;

interface StoredPin {
  v: 1;
  /** Random per-device salt, hex. */
  salt: string;
  /** SHA-256(salt + pin), hex. */
  hash: string;
  /** Digits in the PIN, so the keypad knows when to submit. */
  length: number;
  /** Consecutive wrong attempts since the last success. */
  attempts: number;
}

export type PinVerifyResult =
  | { status: 'ok'; attemptsLeft: number }
  | { status: 'wrong'; attemptsLeft: number }
  | { status: 'locked-out'; attemptsLeft: 0 };

export class PinUnavailableError extends Error {
  constructor() {
    super('Web Crypto is not available in this context');
    this.name = 'PinUnavailableError';
  }
}

// ─── Storage ──────────────────────────────────────────────────────────────────

function readRecord(): StoredPin | null {
  try {
    const raw = window.localStorage.getItem(PIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPin> | null;
    if (
      parsed &&
      parsed.v === 1 &&
      typeof parsed.salt === 'string' &&
      typeof parsed.hash === 'string' &&
      typeof parsed.length === 'number' &&
      typeof parsed.attempts === 'number'
    ) {
      return parsed as StoredPin;
    }
  } catch {
    // Unreadable or blocked storage: behave as if no PIN is set.
  }
  return null;
}

function writeRecord(record: StoredPin): void {
  window.localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(record));
}

function notifyPinChange(): void {
  try {
    window.dispatchEvent(new Event(PIN_CHANGE_EVENT));
  } catch {
    // No window: nothing to notify.
  }
}

export function hasPin(): boolean {
  return readRecord() !== null;
}

/** Digits of the stored PIN, or null when none is set. */
export function getPinLength(): number | null {
  return readRecord()?.length ?? null;
}

export function getAttemptsLeft(): number {
  const record = readRecord();
  return record ? Math.max(0, MAX_PIN_ATTEMPTS - record.attempts) : 0;
}

export function isValidPin(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_MIN_LENGTH},${PIN_MAX_LENGTH}}$`).test(pin);
}

export function clearPin(): void {
  try {
    window.localStorage.removeItem(PIN_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was stored.
  }
  notifyPinChange();
}

// ─── Hashing ──────────────────────────────────────────────────────────────────

/** `crypto.subtle` only exists in secure contexts (HTTPS or localhost). */
export function isPinSupported(): boolean {
  return (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.subtle?.digest === 'function' &&
    typeof globalThis.crypto.getRandomValues === 'function'
  );
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function generateSalt(): string {
  if (!isPinSupported()) throw new PinUnavailableError();
  return toHex(globalThis.crypto.getRandomValues(new Uint8Array(16)));
}

export async function hashPin(pin: string, salt: string): Promise<string> {
  if (!isPinSupported()) throw new PinUnavailableError();
  const data = new TextEncoder().encode(`${salt}${pin}`);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return toHex(new Uint8Array(digest));
}

/** Compares two strings without bailing out at the first difference. */
export function constantTimeEqual(a: string, b: string): boolean {
  let diff = a.length ^ b.length;
  const length = Math.max(a.length, b.length);
  for (let i = 0; i < length; i += 1) {
    diff |= (a.charCodeAt(i) || 0) ^ (b.charCodeAt(i) || 0);
  }
  return diff === 0;
}

// ─── Set / verify ─────────────────────────────────────────────────────────────

/** Stores a new PIN with a fresh salt and a zeroed attempt counter. */
export async function setPin(pin: string): Promise<void> {
  if (!isValidPin(pin)) {
    throw new RangeError(
      `PIN must be ${PIN_MIN_LENGTH} to ${PIN_MAX_LENGTH} digits`
    );
  }
  const salt = generateSalt();
  const hash = await hashPin(pin, salt);
  writeRecord({ v: 1, salt, hash, length: pin.length, attempts: 0 });
  notifyPinChange();
}

/**
 * Checks a PIN. A wrong attempt is counted before it is reported; the fifth
 * consecutive miss erases the PIN (`locked-out`) and the caller signs out.
 * With no PIN set there is nothing to guard, so it succeeds.
 */
export async function verifyPin(pin: string): Promise<PinVerifyResult> {
  const record = readRecord();
  if (!record) return { status: 'ok', attemptsLeft: MAX_PIN_ATTEMPTS };

  const candidate = await hashPin(pin, record.salt);
  const matches = constantTimeEqual(candidate, record.hash);

  const fresh = readRecord();
  if (!fresh) return { status: 'ok', attemptsLeft: MAX_PIN_ATTEMPTS };

  if (matches) {
    if (fresh.attempts !== 0) writeRecord({ ...fresh, attempts: 0 });
    return { status: 'ok', attemptsLeft: MAX_PIN_ATTEMPTS };
  }

  const attempts = fresh.attempts + 1;
  if (attempts >= MAX_PIN_ATTEMPTS) {
    clearPin();
    return { status: 'locked-out', attemptsLeft: 0 };
  }
  writeRecord({ ...fresh, attempts });
  return { status: 'wrong', attemptsLeft: MAX_PIN_ATTEMPTS - attempts };
}

// ─── Auto-lock interval ───────────────────────────────────────────────────────

export function getAutoLock(): AutoLockSetting {
  try {
    const raw = window.localStorage.getItem(AUTOLOCK_STORAGE_KEY);
    if (raw === 'never') return 'never';
    const minutes = Number(raw);
    if (minutes === 1 || minutes === 5 || minutes === 15) return minutes;
  } catch {
    // Storage blocked: use the default.
  }
  return DEFAULT_AUTOLOCK;
}

export function setAutoLock(setting: AutoLockSetting): void {
  try {
    window.localStorage.setItem(AUTOLOCK_STORAGE_KEY, String(setting));
  } catch {
    // Storage blocked: the setting simply does not persist.
  }
}

/** True when the app has been hidden for at least the auto-lock interval. */
export function shouldAutoLock(
  hiddenAt: number,
  now: number,
  setting: AutoLockSetting = getAutoLock()
): boolean {
  if (setting === 'never') return false;
  return now - hiddenAt >= setting * 60_000;
}

// ─── Lock requests ────────────────────────────────────────────────────────────

/** Locks the app now (panic exit). A no-op when no PIN is set. */
export function requestAppLock(): void {
  if (!hasPin()) return;
  try {
    window.dispatchEvent(new Event(LOCK_REQUEST_EVENT));
  } catch {
    // No window: nothing to lock.
  }
}
